import { createInertiaApp } from '@inertiajs/react'
import React, { createElement } from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider } from '@/frontend/contexts/ThemeContext'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import MainLayout from '@/frontend/layouts/MainLayout'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'

type InertiaBeforeVisit = CustomEvent<{
  visit?: {
    headers?: Record<string, string>
  }
}>

type InertiaNavigateEvent = CustomEvent<{
  page?: {
    props?: {
      csrfToken?: string | null
    }
  }
}>

// Temporary type definition, until @inertiajs/react provides one
type PageComponent = React.ComponentType & {
  layout?: (page: React.ReactElement) => React.ReactElement
}

// Export for use in page components
export type { PageComponent }

type ResolvedComponent = {
  default: PageComponent
}

function csrfMeta(): HTMLMetaElement | null {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
}

function currentCsrfToken(): string {
  return csrfMeta()?.content || ''
}

function syncCsrfToken(token?: string | null) {
  if (!token) return

  const meta = csrfMeta() || document.head.appendChild(document.createElement('meta'))
  meta.name = 'csrf-token'
  meta.content = token
}

document.addEventListener('inertia:before', (event) => {
  const csrfToken = currentCsrfToken()
  const visit = (event as InertiaBeforeVisit).detail?.visit

  if (!csrfToken || !visit) return

  visit.headers = {
    ...visit.headers,
    'X-CSRF-Token': csrfToken,
  }
})

document.addEventListener('inertia:navigate', (event) => {
  syncCsrfToken((event as InertiaNavigateEvent).detail?.page?.props?.csrfToken)
})

createInertiaApp({
  // Set default page title
  title: title => title ? `${title} — StationZero` : 'StationZero',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // Auto-apply layout based on page path
    if (page && !page.default.layout) {
      if (name.startsWith('Dashboard/')) {
        page.default.layout = (page: React.ReactElement) => createElement(DashboardLayout, { children: page })
      } else if (name.startsWith('CommandCenter/')) {
        page.default.layout = (page: React.ReactElement) => createElement(CommandCenterLayout, { children: page })
      } else if (name.startsWith('Home/') || name.startsWith('Privacy/') || name.startsWith('Terms/')) {
        page.default.layout = (page: React.ReactElement) => createElement(MainLayout, { children: page })
      }
      // Pages like Checkout can explicitly set their layout or remain layout-less
    }

    return page
  },

  setup({ el, App, props }) {
    syncCsrfToken(props.initialPage.props.csrfToken as string | null | undefined)

    if (el) {
      createRoot(el).render(createElement(ThemeProvider, null, createElement(App, props)))
    } else {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      )
    }
  },
})
